import { resolveFrontendApiBase } from "./utils/apiBase";
import React, { useState, useRef } from "react"
import axios, { AxiosResponse } from "axios"
import { Tag, message, Progress, Tooltip } from "antd"
import { LoadingOutlined, DownloadOutlined } from "@ant-design/icons"

interface AudioDownloadTagProps {
  fileUrl?: string
  filename?: string
  format: string
  forceZip?: boolean
  isChapterZip?: boolean
}

interface DirectDownloadResponse {
  direct_download: boolean
  download_url: string
  filename: string
  file_size: number
  message: string
}

const COOLDOWN_MS = 3000

const AudioDownloadTag: React.FC<AudioDownloadTagProps> = ({
  fileUrl,
  filename,
  format,
  forceZip = false,
  isChapterZip = false,
}) => {
  const [isDownloading, setIsDownloading] = useState(false)
  const [downloadProgress, setDownloadProgress] = useState(0)
  const lastDownloadRef = useRef(0)

  const inferFilename = (url: string): string => {
    try {
      if (!url.startsWith('http')) {
        return filename || "download";
      }
      
      const parts = new URL(url).pathname.split("/");
      return decodeURIComponent(parts[parts.length - 1]);
    } catch {
      return filename || "download";
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes >= 1024 * 1024 * 1024) {
      return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
    } else if (bytes >= 1024 * 1024) {
      return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    } else if (bytes >= 1024) {
      return `${(bytes / 1024).toFixed(1)} KB`;
    } else {
      return `${bytes} bytes`;
    }
  };

  const handleDirectDownload = (downloadUrl: string, downloadFilename: string, fileSize?: number) => {
    const link = document.createElement("a");
    link.href = downloadUrl;
    link.download = downloadFilename;
    link.style.display = 'none';
    
    document.body.appendChild(link);
    link.click();
    
    setTimeout(() => {
      document.body.removeChild(link);
    }, 100);
    
    const sizeText = fileSize ? ` (${formatFileSize(fileSize)})` : '';
    console.log(`Direct download initiated: ${downloadFilename}${sizeText}`);
  };

const handleDownload = async (): Promise<void> => {
  if (!fileUrl) {
    message.error("No file URL provided.");
    return;
  }
  
  const now = Date.now();
  if (now - lastDownloadRef.current < COOLDOWN_MS) return;
  lastDownloadRef.current = now;
  setIsDownloading(true);
  setDownloadProgress(0);

  try {
    let downloadName = filename || inferFilename(fileUrl);
    
    // Check if this is a full audiobook (URL ends with _full.zip)
    const isFullAudiobook = fileUrl.toLowerCase().includes("_full.zip");
    
    // For chapter zip files - direct download
    if (isChapterZip && !forceZip && !isFullAudiobook) {
      console.log(`Downloading chapter zip directly: ${downloadName}`);
      handleDirectDownload(fileUrl, downloadName);
      return;
    }
    
    // For full audiobook ZIP files, keep the .zip extension
    if (isFullAudiobook) {
      // Don't modify the filename for full audiobook ZIPs
      if (!downloadName.toLowerCase().endsWith('.zip')) {
        downloadName = downloadName + '.zip';
      }
    } else {
      // For regular audio files, clean up the filename
      const formatExt = `.${format.toLowerCase()}`;
      downloadName = downloadName.replace(/\.zip$/, '');
      
      // Only add extension if it's not already there
      if (!downloadName.toLowerCase().endsWith(formatExt)) {
        // Remove any existing audio extension before adding the correct one
        downloadName = downloadName.replace(/\.(mp3|wav|ogg|aac|flac|m4b)$/i, '');
        downloadName = downloadName + formatExt;
      }
    }
    
    //console.log(`Using backend endpoint for: ${downloadName}`);
    
    // Make a single request with responseType as 'arraybuffer' which can handle both JSON and binary
    const response = await axios.get(
      `${resolveFrontendApiBase()}/download-audiofile/`,
      {
        params: { 
          file_url: fileUrl,
          filename: downloadName 
        },
        responseType: "arraybuffer", // Use arraybuffer instead of blob
        timeout: 600000, // 10 minutes for large files
        maxContentLength: 5 * 1024 * 1024 * 1024, // 5GB limit
        maxBodyLength: 5 * 1024 * 1024 * 1024, // 5GB limit
        onDownloadProgress: (progressEvent) => {
          if (progressEvent.total) {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setDownloadProgress(percentCompleted);
            console.log(`Download progress: ${percentCompleted}%`);
          } else {
            setDownloadProgress(-1);
          }
        }
      }
    );
    
    // Check if response is JSON by looking at content-type header
    const responseContentType = response.headers['content-type'];
    const contentType = typeof responseContentType === 'string' ? responseContentType : undefined;
    
    if (contentType?.includes('application/json')) {
      // Convert arraybuffer to string to parse JSON
      const decoder = new TextDecoder();
      const jsonString = decoder.decode(response.data);
      const directDownloadData = JSON.parse(jsonString) as DirectDownloadResponse;
      
      if (directDownloadData.direct_download) {
        console.log("Received direct download info for large file");
        handleDirectDownload(
          directDownloadData.download_url, 
          directDownloadData.filename,
          directDownloadData.file_size
        );
        return;
      }
    } else {
      // Handle as binary data
      const blob = new Blob([response.data], { type: contentType || 'application/octet-stream' });
      const link = document.createElement("a");
      link.href = window.URL.createObjectURL(blob);
      
      // Extract filename from Content-Disposition header if available
      const responseDisposition = response.headers['content-disposition'];
      const contentDisposition = typeof responseDisposition === 'string' ? responseDisposition : undefined;
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
        if (filenameMatch) {
          link.download = filenameMatch[1];
        } else {
          link.download = downloadName;
        }
      } else {
        link.download = downloadName;
      }
      
      document.body.appendChild(link);
      link.click();
      
      // Clean up
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(link.href);
      }, 100);
      
      console.log(`Successfully downloaded: ${link.download}`);
      message.success("Download completed successfully");
    }
    
  } catch (err) {
    console.error("Download failed", err);
    if (axios.isAxiosError(err)) {
      if (err.response?.status === 400) {
        message.error("Download link has expired. Please refresh the page and try again.");
      } else if (err.response?.status === 413) {
        message.error("File is too large to download through server. Please try direct download.");
      } else if (err.response?.status === 504) {
        message.error("Download timeout - file is too large. Please try again later.");
      } else if (err.code === 'ECONNABORTED') {
        message.error("Download timeout. Please try again.");
      } else {
        message.error(`Download failed: ${err.response?.statusText || err.message}`);
      }
    } else {
      message.error("Failed to download file. Please try again.");
    }
  } finally {
    setIsDownloading(false);
    setDownloadProgress(0);
  }
};

  const tagContent = (
    <>
      {isDownloading ? (
        downloadProgress === -1 ? (
          <LoadingOutlined spin />
        ) : (
          <span style={{ minWidth: '40px', display: 'inline-block' }}>
            {downloadProgress}%
          </span>
        )
      ) : (
        <DownloadOutlined />
      )}{" "}
      {format.toUpperCase()}
    </>
  );

  const tag = (
    <Tag
      color="cyan"
      onClick={handleDownload}
      style={{
        cursor: isDownloading ? "not-allowed" : "pointer",
        opacity: isDownloading ? 0.7 : 1,
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
        userSelect: "none",
        minWidth: isDownloading ? '100px' : 'auto',
      }}
    >
      {tagContent}
    </Tag>
  );

  // Show progress bar in tooltip when downloading
  if (isDownloading && downloadProgress > 0 && downloadProgress < 100) {
    return (
      <Tooltip
        title={
          <div style={{ width: 200 }}>
            <Progress 
              percent={downloadProgress} 
              size="small" 
              strokeColor="#52c41a"
              format={percent => `${percent}%`}
            />
            <div style={{ marginTop: 4, fontSize: 12 }}>
              Downloading {format.toUpperCase()}...
            </div>
          </div>
        }
        open={true}
        placement="top"
      >
        {tag}
      </Tooltip>
    );
  }

  return tag;
}

export default AudioDownloadTag;
